import { DefaultResponseWithData } from "./system";
import { IPlannerVehicleRow, VehicleProductionLine } from "./ProductionPlanner";
import { DismantlingBayType } from "./DismantlingBay";
import { VehicleComplexity } from "./Vehicle";
import { PartAssemblyType } from "./Part";

export { PartAssemblyType };

export enum ProductionDashboardBayStatus {
  Available = 0,
  InProgress = 1,
  Complete = 2,
}

/** Distinguishes a real assembly column from the two optional non-assembly stages. */
export enum ProductionDashboardAssemblyKind {
  Assembly = 0,
  Triage = 1,
  Depollution = 2,
}

export interface IProductionDashboardAssembly {
  name: string;
  type: PartAssemblyType;
  kind: ProductionDashboardAssemblyKind;
}

export interface IBaysInUseKpi {
  used: number;
  total: number;
}

export interface IPartsRemovedBreakdownEntry {
  name: string;
  count: number;
}

export interface IPartsRemovedKpi {
  total: number;
  breakdown: IPartsRemovedBreakdownEntry[];
}

export interface IProductionDashboardKpis {
  baysInUse: IBaysInUseKpi;
  linesInUse: IBaysInUseKpi;
  vehiclesInProgress: number;
  vehiclesDismantledToday: number;
  partsRemoved: IPartsRemovedKpi;
}

export interface IProductionDashboardBayVehicle {
  id: string;
  vrm: string;
  stockNumber: string | null;
  combinedMake: string | null;
  combinedModel: string | null;
  bodyStyle: string | null;
  complexity: VehicleComplexity;
  productionLine: VehicleProductionLine;
  partsRemoved: number;
  partsTotal: number;
  percentComplete: number;
  currentAssemblyName: string | null;
  currentAssemblyIndex: number | null;
  assemblyCount: number;
  currentAssemblyPartsRemoved: number;
  currentAssemblyPartsTotal: number;
  currentAssemblyElapsedSeconds: number;
  currentAssemblyStartedOn: string | null;
  currentAssemblyAverageMinutes: number | null;
  currentAssemblyWarningMinutes: number | null;
}

export interface IProductionDashboardBay {
  id: string;
  name: string;
  type: DismantlingBayType;
  line: VehicleProductionLine;
  status: ProductionDashboardBayStatus;
  currentVehicle: IProductionDashboardBayVehicle | null;
  currentVehicles: IProductionDashboardBayVehicle[];
  dueNext: IPlannerVehicleRow[];
}

export interface IProductionDashboardSession {
  id: string;
  vehicleId: string;
  vrm: string | null;
  stockNumber: string | null;
  combinedMake: string | null;
  combinedModel: string | null;
  elapsedSeconds: number;
  startedOn: string | null;
  /** The bay/line the vehicle is on today, or null when it isn't allocated to one. */
  dismantlingBayId: string | null;
}

export interface IProductionDashboardOverview {
  kpis: IProductionDashboardKpis;
  bays: IProductionDashboardBay[];
  assemblies: IProductionDashboardAssembly[];
  inProgressTriages: IProductionDashboardSession[];
  inProgressDepollutions: IProductionDashboardSession[];
}

export interface IProductionDashboardOverviewResponse
  extends DefaultResponseWithData<IProductionDashboardOverview> {}

export type ProductionDashboardView = "Line" | "Bays";

/** Median time a vehicle takes through one station over a chosen period. */
export interface IStationCycleTime {
  /** Null for the Triage and Depollution rows: they are not assemblies. */
  partAssemblyId: string | null;
  name: string;
  type: PartAssemblyType;
  /**
   * Whether this row is a real assembly or one of the two stages. The stage labels are rendered
   * from this rather than from name, so they read in the user language like the flow does.
   */
  kind: ProductionDashboardAssemblyKind;
  /** Flow order, so the table reads in the same direction as the line. */
  order: number;
  /** Vehicles that finished this station in the period — the sample size. */
  vehicleCount: number;
  /**
   * Median worked seconds per vehicle, or null when nothing came through in the period.
   * Null and 0 mean different things: "nobody used this station" vs "it took no time".
   */
  medianSeconds: number | null;
  /** The station's target, already converted from the minutes the API stores. */
  targetSeconds: number | null;
  /**
   * Median seconds a vehicle waited after finishing here before the next station started - the
   * queue between stations rather than the work at one. Null when nothing could be measured.
   */
  medianIdleSeconds: number | null;
  /**
   * False where there is nothing after the row to wait for: the last station in the flow, and the
   * two stages while they sit outside it. Those show N/A rather than a dash.
   */
  measuresIdle: boolean;
}

export interface IStationCycleTimes {
  from: string;
  to: string;
  stations: IStationCycleTime[];
}

export interface IStationCycleTimesResponse
  extends DefaultResponseWithData<IStationCycleTimes> {}
